import type { AppState, AppAction, ThunkExtras } from '../types/app.types';
import type { Store, Reducer } from 'redux';
import { createStore, applyMiddleware, compose } from 'redux';
import thunk from 'redux-thunk';
import pick from 'lodash/pick';
import { createTransform, persistCombineReducers, PURGE } from 'redux-persist';
import localForage from 'localforage';
import * as reducers from './modules';
import { DEV, IS_BROWSER, IS_CLIENT_BUILD } from '../config';

/**
 * We need to persist those cart properties as they're not available
 * from the backend response. This is needed for the temporary hybrid
 * mobile-web + rx-web process (responsive redirections).
 *
 * ! TODO: Remove below transform after rx-web will be unused
 * ! and the migration to mobile-web will be finished
 */
const CART_MODULE_PROPERTY_WHITELIST = ['confirmedPrescriptions', 'isBuyingConsultations'];

const cartModuleTransform = createTransform(
  (state: Record<string, any>) => pick(state, CART_MODULE_PROPERTY_WHITELIST),
  (state: Record<string, any>) => pick(state, CART_MODULE_PROPERTY_WHITELIST),
  {
    whitelist: ['cart'],
  }
);

/**
 * redux presist
 * Stores the whitelisted keys to the device
 */
const reduxPersistConfig = {
  // This is where we set the storage engine used to persist data
  // We use localForage which selects backend drivers for the datastore, based on availability, in this order
  // 1. IndexedDB
  // 2. WebSQL
  // 3. localStorage
  storage: localForage,
  key: 'pwa-v4:',
  debug: DEV,
  transforms: [cartModuleTransform],
  // Here we give a list of reducer keys we would like to persist.
  // before adding a key, read: /src/store/modules/README.md
  whitelist: ['accountMedications', 'paymentMethods', 'blinkCard', 'pharmacy', 'subscriptions', 'search', 'cart'],
};

const errorHandler = (error) => {
  /**
   * We do not want to report API errors to NewRelic
   * We just want to have plain JS type/syntax/support errors
   */
  if (!error.status) {
    console.error(error);
    window.newrelic && window.newrelic.noticeError(error);
  }
  throw error;
};

const errorHandlerMiddleware = () => (next) => (action) => {
  let result;

  try {
    result = next(action);
  } catch (error) {
    errorHandler(error);
  }

  if (result && typeof result.then === 'function') {
    return result.catch(errorHandler);
  }

  return result;
};

function _createStore(preloadedState: any, thunkExtras: ThunkExtras): Store<AppState, AppAction> {
  const reducer: Reducer<AppState, AppAction> = persistCombineReducers(reduxPersistConfig, reducers);

  /**
   * We need a root reducer to listen for the redux-persist PURGE action.
   * So that in addition to purging the state from localStorage, we purge also the local state.
   * Currently, purging happens only on user logout.
   */
  const rootReducer = (state, action) => {
    if (action.type === PURGE) {
      // Reset every store except...
      state = pick(state, ['header', 'session.hydrated', 'session.initialized']);
    }

    return reducer(state, action);
  };

  let store: Store<AppState, AppAction>;
  const funcs = [applyMiddleware(errorHandlerMiddleware, thunk.withExtraArgument(thunkExtras))];

  if (IS_BROWSER && DEV) {
    if (IS_CLIENT_BUILD) {
      window.localForage = localForage;

      if (window.__REDUX_DEVTOOLS_EXTENSION__) {
        funcs.push(window.__REDUX_DEVTOOLS_EXTENSION__());
      }
    }

    store = createStore(rootReducer, preloadedState, compose(...funcs));
  } else {
    store = createStore(rootReducer, preloadedState, compose(...funcs));
  }

  return store;
}

export default _createStore;
